import { Input, Tooltip, TooltipTrigger } from "@light-technology/components";
import { Lock01, Sun } from "@untitledui/icons";
import { useController, type Control } from "react-hook-form";
import type { PlanDetailResponse } from "../../state/api.types";
import { PARTNER_SUCCESS_EMAIL } from "../../utilities/constants";
import { formatCents } from "../../utilities/currency";
import { usePlanConstraintByFieldType, type FormValues } from "./common";
import { ViewOnlyField } from "./ViewOnlyField";

type EditSolarBuybackProps = {
  plan: PlanDetailResponse;
  control: Control<FormValues>;
  isEditingDisabled: boolean;
};

export const EditSolarBuyback = ({
  plan,
  control,
  isEditingDisabled,
}: EditSolarBuybackProps) => {
  const constraintByFieldType = usePlanConstraintByFieldType(plan);
  const exportRateConstraint = constraintByFieldType.EXPORT_RATE;
  const { field: exportRateField, fieldState: exportRateFieldState } =
    useController({
      control,
      name: "exportRate",
      rules: exportRateConstraint && {
        min: exportRateConstraint.min_value,
        max: exportRateConstraint.max_value,
      },
    });

  const planType = plan.plan_type?.plan_type ?? "";
  const isRealTime = planType === "RTW_SOLAR_BUYBACK";
  const isMatched = ["SOLAR_MATCHED", "MATCHED_SOLAR_BUYBACK"].includes(
    planType,
  );
  const isUnmatched = ["SOLAR_UNMATCHED", "UNMATCHED_SOLAR_BUYBACK"].includes(
    planType,
  );

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-sm">
        <div className="flex flex-row items-center gap-md">
          <div className="flex w-full flex-row items-center gap-md">
            <Sun />
            <div className="w-full text-lg font-semibold">Solar Buyback</div>
          </div>
          {isEditingDisabled && (
            <Tooltip
              arrow
              title={`Editing is not available for this plan. Please reach out to us at ${PARTNER_SUCCESS_EMAIL} for assistance.`}
            >
              <TooltipTrigger>
                <Lock01 />
              </TooltipTrigger>
            </Tooltip>
          )}
        </div>
        <div className="text-sm text-tertiary">
          Let customers earn energy credits from the excess energy their solar
          panels produce.
        </div>
      </div>
      {isRealTime && (
        <ViewOnlyField label="Buyback rate" value="Real-time wholesale" />
      )}
      {!isRealTime && isMatched && (
        <ViewOnlyField
          label="Buyback rate"
          value="Matched to energy rate"
          description="Reach out to us to create an unmatched buyback rate plan."
        />
      )}
      {!isRealTime &&
        isUnmatched &&
        (isEditingDisabled ? (
          <ViewOnlyField
            label="Buyback rate"
            value={`${formatCents(exportRateField.value || 0)}¢ per kWh`}
          />
        ) : (
          <div className="flex flex-row items-center gap-md">
            <Input
              inputClassName="text-right"
              label="Buyback rate"
              hint={
                exportRateConstraint &&
                `Can be between ${formatCents(exportRateConstraint.min_value)}¢-${formatCents(exportRateConstraint.max_value)}¢ per kWh.`
              }
              isRequired
              isInvalid={exportRateFieldState.invalid}
              {...exportRateField}
            />
            <span className="text-sm text-nowrap text-tertiary">¢ per kWh</span>
          </div>
        ))}
    </div>
  );
};
